import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { sendPartnershipRequest } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Handshake, Building, Users, TrendingUp, Globe } from 'lucide-react';

const PartnershipPage = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    partnership_type: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await sendPartnershipRequest(formData);
      toast.success(t('messageSent'));
      setFormData({ company_name: '', contact_name: '', email: '', phone: '', partnership_type: '', message: '' });
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const partnershipTypes = [
    { value: 'agency', label: language === 'fr' ? 'Agence immobilière' : 'Real estate agency' },
    { value: 'hotel', label: language === 'fr' ? 'Hôtel / Résidence' : 'Hotel / Residence' },
    { value: 'sponsor', label: language === 'fr' ? 'Sponsor / Publicité' : 'Sponsor / Advertising' },
    { value: 'tech', label: language === 'fr' ? 'Partenaire technologique' : 'Technology partner' },
    { value: 'other', label: language === 'fr' ? 'Autre' : 'Other' },
  ];

  const benefits = [
    {
      icon: Globe,
      title: language === 'fr' ? 'Visibilité Africaine' : 'African Visibility',
      description: language === 'fr' 
        ? 'Accédez à un marché en pleine croissance avec des millions de voyageurs potentiels.'
        : 'Access a growing market with millions of potential travelers.'
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Réseau Qualifié' : 'Qualified Network',
      description: language === 'fr'
        ? 'Connectez-vous avec des étudiants, professionnels et voyageurs à la recherche de logements.'
        : 'Connect with students, professionals and travelers looking for accommodation.'
    },
    {
      icon: TrendingUp,
      title: language === 'fr' ? 'Croissance Partagée' : 'Shared Growth',
      description: language === 'fr'
        ? 'Bénéficiez de notre expansion à travers le Cameroun et l\'Afrique.'
        : 'Benefit from our expansion across Cameroon and Africa.'
    },
    {
      icon: Building,
      title: language === 'fr' ? 'Support Dédié' : 'Dedicated Support',
      description: language === 'fr'
        ? 'Un accompagnement personnalisé pour maximiser votre présence sur Roomia.net.'
        : 'Personalized support to maximize your presence on Roomia.net.'
    },
  ];

  return (
    <div className="min-h-screen py-16 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Handshake className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-testid="partnership-title">
            {t('partnershipTitle')}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('partnershipSubtitle')}
          </p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Form */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{language === 'fr' ? 'Demande de partenariat' : 'Partnership request'}</CardTitle>
            <CardDescription>
              {language === 'fr'
                ? 'Remplissez ce formulaire et notre équipe vous contactera dans les 48 heures.'
                : 'Fill out this form and our team will contact you within 48 hours.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">{t('companyName')} *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    data-testid="partnership-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">{language === 'fr' ? 'Nom du contact' : 'Contact name'} *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    required
                    data-testid="partnership-contact-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="partnership-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="partnership-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnership_type">{t('partnershipType')} *</Label>
                <Select
                  value={formData.partnership_type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, partnership_type: v }))}
                >
                  <SelectTrigger data-testid="partnership-type-select">
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionnez un type' : 'Select a type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {partnershipTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">{t('message')} *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder={language === 'fr' 
                    ? 'Décrivez votre projet de partenariat...'
                    : 'Describe your partnership project...'}
                  required
                  data-testid="partnership-message-input"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full" data-testid="partnership-submit-btn">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('send')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PartnershipPage;
